import { User, Role } from '../types';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';

export const userService = {
  /**
   * Obtiene la lista completa de usuarios (personal) ordenada por fecha de creación.
   */
  async fetchUsers(): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Crea un usuario en Supabase Auth y en la tabla pública de usuarios.
   * Utiliza supabaseAdmin para no afectar la sesión actual del administrador en el navegador.
   */
  async addUser(user: Omit<User, 'id'>, initialPassword?: string): Promise<User> {
    // 1. Crear el usuario en Supabase Auth
    const { data: authData, error: authError } = await supabaseAdmin.auth.signUp({
      email: user.email!,
      password: initialPassword || 'TMinus2026',
      options: {
        data: { name: user.name, role: user.role }
      }
    });

    if (authError || !authData.user) {
      console.error('Error creando usuario en Auth:', authError);
      throw new Error(authError?.message || 'Error desconocido creando perfil Auth');
    }

    // 2. Insertar los datos públicos vinculando el id autogenerado por Auth
    const { data, error } = await supabase
      .from('users')
      .insert({
        id: authData.user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        avatar: user.avatar
      })
      .select()
      .single();

    if (error) {
      console.error('Error insertando usuario en tabla pública:', error);
      throw error;
    }

    return data;
  },

  /**
   * Actualiza el perfil de un usuario existente.
   */
  async updateUser(user: User): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({
        name: user.name,
        email: user.email,
        role: user.role,
        avatar: user.avatar
      })
      .eq('id', user.id);

    if (error) throw error;
  },

  /**
   * Elimina un usuario por su ID de la tabla pública.
   * Nota: En producción, puede requerirse también eliminarlo de auth.users si es necesario.
   */
  async deleteUser(userId: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId);

    if (error) throw error;
  }
};
